import type { AmazonConnectProvider } from "@amazon-connect/core";
import { registry, type ClientModule } from "../registry";
import { createLogger } from "./logger";

const log = createLogger("gateway");

/** The single seam between the kit's core and the Amazon Connect SDK. */
export interface SdkGateway {
  readonly mode: "live" | "mock";
  isReady(): boolean;
  getClient(moduleId: string): Record<string, unknown> | undefined;
}

/**
 * Constructs real SDK clients from a provider, lazily and once per module.
 *
 * The `app` module is `kind: "lifecycle"`: `AmazonConnectApp.init()` returns
 * `{ provider: AmazonConnectApp }` and the provider IS the app instance, carrying
 * `onStart`/`onStop`/`onError`/`offError` directly (amendment A8). There is no separate
 * app-instance constructor parameter — `getClient("app")` returns the provider itself.
 */
export class LiveGateway implements SdkGateway {
  readonly mode = "live" as const;
  private readonly clients = new Map<string, Record<string, unknown>>();

  constructor(private readonly provider: AmazonConnectProvider) {}

  /** Live mode is always ready: the SDK provider exists as soon as the gateway does. */
  isReady(): boolean {
    return true;
  }

  /** Returns the cached or newly constructed client for a registry module id. */
  getClient(moduleId: string): Record<string, unknown> | undefined {
    const cached = this.clients.get(moduleId);
    if (cached) return cached;
    const mod = registry.find((m) => m.id === moduleId);
    if (!mod) return undefined;
    if (mod.kind === "lifecycle") {
      const client = this.provider as unknown as Record<string, unknown>;
      this.clients.set(moduleId, client);
      return client;
    }
    try {
      const client = mod.create(this.provider);
      this.clients.set(moduleId, client);
      return client;
    } catch (e) {
      log.error(`failed to construct client for ${moduleId}`, e);
      return undefined;
    }
  }
}

type Handler = (payload: unknown) => void;

/**
 * Generates its clients from the registry descriptors, so mock mode automatically covers
 * every declared member and cannot drift from the registry. Every on/off pair accepts and
 * ignores any trailing arguments beyond the handler, since a small number of real SDK events
 * (e.g. `onAIAgentContactReady(handler, contactId)`) require a trailing scope argument that
 * this mock has no need to filter on.
 */
export class MockGateway implements SdkGateway {
  readonly mode = "mock" as const;
  private readonly handlers = new Map<string, Set<Handler>>();
  private readonly clients = new Map<string, Record<string, unknown>>();

  constructor(private readonly modules: readonly ClientModule[] = registry) {
    for (const m of this.modules) this.clients.set(m.id, this.buildClient(m));
  }

  /** Mock mode is always ready: there is no handshake to wait for. */
  isReady(): boolean {
    return true;
  }

  /** Returns the generated client for a registry module id. */
  getClient(moduleId: string): Record<string, unknown> | undefined {
    return this.clients.get(moduleId);
  }

  /**
   * Delivers a synthetic event to everything subscribed to it. Each handler is isolated: one
   * throwing handler must never stop its siblings from receiving the same payload, since this
   * kit's entire purpose is reliably recording every SDK event (matches the isolation pattern
   * already used by `EventLog.notify()` and `connection.ts`'s `transition()`).
   */
  emit(moduleId: string, eventName: string, payload: unknown): void {
    for (const h of this.handlers.get(`${moduleId}:${eventName}`) ?? []) {
      try {
        h(payload);
      } catch (e) {
        log.error(`handler for ${moduleId}:${eventName} threw`, e);
      }
    }
  }

  private buildClient(m: ClientModule): Record<string, unknown> {
    const client: Record<string, unknown> = {};
    for (const e of m.events) {
      const key = `${m.id}:${e.name}`;
      client[e.name] = (h: Handler, ..._scope: unknown[]) => {
        if (!this.handlers.has(key)) this.handlers.set(key, new Set());
        this.handlers.get(key)?.add(h);
      };
      client[e.off] = (h: Handler, ..._scope: unknown[]) => {
        this.handlers.get(key)?.delete(h);
      };
    }
    for (const r of m.requests) {
      client[r.name] = async (...args: unknown[]) => ({
        __mock: true,
        module: m.id,
        method: r.name,
        args,
      });
    }
    return client;
  }
}
